import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  useOnlineStatus,
  obtenerEstadoSincronizacionBac,
  reintentarPendienteBac,
  descartarUnPendienteBac,
  descartarPendientesConErrorBac,
  limpiarTodosPendientesBac,
  sincronizarPendientesBac,
} from "../../hooks/useSync";

const ETIQUETAS_TIPO = {
  pendiente: { label: "⏳ Pendiente", color: "#854F0B", bg: "#FFFBF0", border: "#EF9F27" },
  error:     { label: "⚠️ Error",     color: "#A32D2D", bg: "#FCEBEB", border: "#F09595" },
};

/**
 * Panel "📋 Ver pendientes" para BAC Sarampión — SIEMPRE visible mientras
 * haya algo en la cola de visitas sin sincronizar. Muestra el motivo
 * REAL de cada error (lo que devolvió el backend), no solo "falló" —
 * así se puede ver de un vistazo qué está atascado y por qué.
 *
 * Móntalo en BacSarampionHome.jsx o al inicio de TablaBacSarampion.jsx.
 */
export default function EstadoSincronizacionBac() {
  const online = useOnlineStatus();
  const [estado, setEstado] = useState({ pendientes: 0, errores: 0, detalleErrores: [], detallePendientes: [] });
  const [sincronizando, setSincronizando] = useState(false);
  const [expandido, setExpandido] = useState(false);

  const cargarEstado = useCallback(async () => {
    const e = await obtenerEstadoSincronizacionBac();
    setEstado(e);
  }, []);

  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  useEffect(() => {
    const handler = () => cargarEstado();
    window.addEventListener("sync-completado", handler);
    window.addEventListener("bac-error-sincronizacion", handler);
    return () => {
      window.removeEventListener("sync-completado", handler);
      window.removeEventListener("bac-error-sincronizacion", handler);
    };
  }, [cargarEstado]);

  const reintentarAhora = async () => {
    if (!online) {
      Swal.fire("Sin conexión", "Necesitas conexión a internet para sincronizar.", "warning");
      return;
    }
    setSincronizando(true);
    try {
      await sincronizarPendientesBac();
      Swal.fire({ icon: "success", title: "Sincronización completada", timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire("No se pudo sincronizar todo", "Revisa el detalle abajo — puede haber registros con error que necesitan tu atención.", "warning");
    } finally {
      await cargarEstado();
      setSincronizando(false);
    }
  };

  const reintentarUno = async (item) => {
    await reintentarPendienteBac(item.tabla, item.id);
    await cargarEstado();
    if (online) reintentarAhora();
  };

  const descartarUno = async (item) => {
    const ok = await Swal.fire({
      title: "¿Descartar este registro?",
      text: `${item.referencia} — este cambio local no llegó a guardarse en el servidor y se perderá.`,
      icon: "warning",
      showCancelButton: true, confirmButtonText: "Sí, descartar", cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!ok.isConfirmed) return;
    await descartarUnPendienteBac(item.tabla, item.id);
    await cargarEstado();
  };

  // MUY DESTRUCTIVO — puede perder cambios hechos offline que nunca
  // llegaron al servidor. Es la cola de pendientes en sí.
  const borrarTodosPendientes = async () => {
    const ok = await Swal.fire({
      title: `¿Vaciar la cola de pendientes por completo?`,
      html: `<div style="text-align:left;font-size:13px">Esto borra <strong>todas</strong> las visitas y constancias de Disentimiento PAI que están esperando sincronizarse (${estado.pendientes} pendiente(s), ${estado.errores} con error) — sin importar su estado.<br/><br/>Las que todavía no se guardaron en el servidor <strong>se pierden para siempre</strong>. Esta acción no se puede deshacer.</div>`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, vaciar la cola", cancelButtonText: "Cancelar", confirmButtonColor: "#dc3545",
    });
    if (!ok.isConfirmed) return;

    await limpiarTodosPendientesBac();
    window.location.reload();
  };

  const descartarTodosLosErrores = async () => {
    const primero = await Swal.fire({
      title: `¿Descartar ${estado.errores} registro(s) con error?`,
      html: `<div style="text-align:left;font-size:13px">Estos registros <strong>nunca llegaron a guardarse en el servidor</strong> y no se pueden arreglar reintentando tal cual están. Al descartarlas se pierden permanentemente los datos que tenías guardados solo en este dispositivo.<br/><br/>¿Seguro que quieres continuar?</div>`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, entiendo el riesgo", cancelButtonText: "Cancelar", confirmButtonColor: "#dc3545",
    });
    if (!primero.isConfirmed) return;

    const segundo = await Swal.fire({
      title: "Confirmación final",
      text: "Esta acción no se puede deshacer. ¿Descartar de todas formas?",
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, descartar definitivamente", cancelButtonText: "Cancelar", confirmButtonColor: "#dc3545",
    });
    if (!segundo.isConfirmed) return;

    const n = await descartarPendientesConErrorBac();
    await cargarEstado();
    Swal.fire({ icon: "success", title: `${n} registro(s) descartado(s)`, timer: 1500, showConfirmButton: false });
  };

  const totalItems = estado.pendientes + estado.errores;
  const todosLosItems = [...estado.detallePendientes, ...estado.detalleErrores];

  if (totalItems === 0) return null;

  const hayProblemas = estado.errores > 0;

  return (
    <div style={{
      border: `1.5px solid ${hayProblemas ? "#F09595" : "#dee2e6"}`,
      background: hayProblemas ? "#FCEBEB" : "#f5f6f0",
      borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12.5,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ color: hayProblemas ? "#A32D2D" : "#374151", fontWeight: 700 }}>
          {hayProblemas ? "⚠️ " : "📋 "}
          {estado.pendientes > 0 && `${estado.pendientes} pendiente(s)`}
          {estado.pendientes > 0 && estado.errores > 0 && " · "}
          {estado.errores > 0 && `${estado.errores} con error`}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setExpandido(p => !p)}
            style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 6, padding: "5px 12px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            📋 {expandido ? "Ocultar" : "Ver"} pendientes
          </button>
          {online && estado.pendientes > 0 && (
            <button onClick={reintentarAhora} disabled={sincronizando}
              style={{ background: "#16342a", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11.5, fontWeight: 700, cursor: sincronizando ? "not-allowed" : "pointer", opacity: sincronizando ? 0.6 : 1 }}>
              {sincronizando ? "Sincronizando..." : "🔄 Sincronizar ahora"}
            </button>
          )}
          <button onClick={borrarTodosPendientes}
            title="Borra por completo la cola de pendientes — puede perder visitas sin sincronizar"
            style={{ background: "#fff", border: "1px solid #dc3545", color: "#dc3545", borderRadius: 6, padding: "5px 12px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            ⚠️ Vaciar cola de pendientes
          </button>
        </div>
      </div>

      {expandido && (
        <div style={{ marginTop: 10, borderTop: "1px solid #dee2e6", paddingTop: 8 }}>
          {todosLosItems.length === 0 ? (
            <div style={{ color: "#6c757d", fontSize: 12, padding: "6px 0" }}>Sin registros para mostrar.</div>
          ) : (
            todosLosItems.map((item, i) => {
              const et = ETIQUETAS_TIPO[item.tipo] || ETIQUETAS_TIPO.pendiente;
              return (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                  padding: "7px 10px", marginBottom: 6, borderRadius: 8,
                  background: et.bg, border: `1px solid ${et.border}`,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, color: et.color, fontSize: 12 }}>
                      {et.label} — {item.referencia}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 11 }}>{item.motivo}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {item.tipo === "error" && (
                      <button onClick={() => reintentarUno(item)}
                        style={{ background: "#fff", border: "1px solid #EF9F27", color: "#854F0B", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                        🔄 Reintentar
                      </button>
                    )}
                    <button onClick={() => descartarUno(item)}
                      style={{ background: "#fff", border: "1px solid #dc3545", color: "#dc3545", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                      🗑️ Descartar
                    </button>
                  </div>
                </div>
              );
            })
          )}
          {estado.errores > 0 && (
            <button onClick={descartarTodosLosErrores}
              style={{ marginTop: 4, background: "#fff", border: "1px solid #dc3545", color: "#dc3545", borderRadius: 6, padding: "5px 12px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
              ⚠️ Descartar todos los registros con error
            </button>
          )}
        </div>
      )}
    </div>
  );
}
